import React from 'react'
import moment from 'moment'
import Divider from 'src/components/ui/Divider'
import ExclamationCircle2 from 'src/components/icons/ui/ExclamationCircle2'
import MapPin from 'src/components/icons/ui/MapPin'
import { ILocationV2, IPortalProduct, IService } from 'src/types'
import { IPortalPassenger } from 'src/types/rider-portal'
import { RIDER_PORTAL_UI_MESSAGES } from 'src/constants/rider-portal'
import Calendar3 from 'src/components/icons/ui/Calendar3'
import Clock from 'src/components/icons/ui/Clock'

interface IConfirmAndPay {
  accessibilities: string[]
  dateTime: {
    date: Date | string
    time: Date | string
  }
  locations: {
    dropOff: ILocationV2 | null
    pickUp: ILocationV2 | null
  }
  product: IPortalProduct | null
  service: IService | null
  specialInstructions: string
  tripRequestRiders: IPortalPassenger[]
  setCurrentStep: (step: number) => void
}

const ConfirmAndPay = ({
  accessibilities,
  dateTime,
  locations,
  product,
  service,
  specialInstructions,
  tripRequestRiders,
  setCurrentStep
}: IConfirmAndPay) => {
  const { dropOff, pickUp } = locations
  const { common, trips } = RIDER_PORTAL_UI_MESSAGES
  const TITLE_CLASSES = 'cursor--pointer font-black3 font-medium rp-mb4 rp-my0 text-center text-md'

  return (
    <>
      <div className='bg-muted b-rad-8 rp-mt24 rp-p16'>
        <div className='flex justify-between'>
          <span className='font-black1 font-medium text-sm'>{trips.estimatedFare}</span>
          <span className='font-black1 font-bold text-lg'>
            {product ? `$${(product.fare / 100).toFixed(2)}` : '$0.00'}
          </span>
        </div>
        <p className='font-black1 mb0 rp-mt4 text-center text-xs'>{trips.finalFareCalculatedAfterTrip}</p>
      </div>

      <div className='b-rad-8 rp-mt32 rp-p16 rp-trip-summary-banner text-center'>
        <h3 className='font-green1 font-medium rp-my0 text-lg'>{trips.tripSummary}</h3>
        <p className='font-grey8 rp-my0 rp-mt4 text-md'>{trips.pleaseReviewYourDetailsBelow}</p>
      </div>

      <div className='rp-mt24 rp-trip-summary-details'>
        <div className='trip-summary-service'>
          <h4 className={TITLE_CLASSES} onClick={() => setCurrentStep(1)}>
            {common.service}
          </h4>
          <p className='font-grey8 rp-mt4 rp-my0 text-center text-md'>{service ? service.name : ''}</p>
        </div>
        <div className='trip-summary-passengers'>
          <h4 className={TITLE_CLASSES} onClick={() => setCurrentStep(2)}>
            {`${common.passengers} and ${trips.paymentMethods}`}
          </h4>
          <div className='rp-mt4'>
            {!!tripRequestRiders.length &&
              tripRequestRiders.map(({ fareType, riderType, totalRider }) => (
                <div className='rp-passenger-container rp-mt4' key={`${riderType}-${fareType}`}>
                  <p className='font-grey8 rp-my0 text-md'>{riderType}</p>
                  <p className='font-grey8 rp-my0 text-md'>{fareType}</p>
                  <p className='font-grey8 rp-my0 text-md'>{totalRider}</p>
                </div>
              ))}
          </div>
        </div>
        <div className='trip-summary-pickup'>
          <h4 className={TITLE_CLASSES} onClick={() => setCurrentStep(1)}>
            {trips.pickupLocation}
          </h4>
          <div className='column-gap-8 flex items-center'>
            <div className='flex items-center rp-icon-container'>
              <MapPin className='icon--20x20 svg-stroke-grey8' />
            </div>
            <p className='font-grey8 rp-mt4 rp-my0 text-md'>{pickUp ? pickUp.fullAddress : ''}</p>
          </div>
        </div>
        <div className='trip-summary-destination'>
          <h4 className={TITLE_CLASSES} onClick={() => setCurrentStep(2)}>
            {trips.destination}
          </h4>
          <div className='column-gap-8 flex items-center'>
            <div className='flex items-center rp-icon-container'>
              <MapPin className='icon--20x20 svg-stroke-grey8' />
            </div>
            <p className='font-grey8 rp-mt4 rp-my0 text-md'>{dropOff ? dropOff.fullAddress : ''}</p>
          </div>
        </div>
        {dateTime.date && dateTime.time && (
          <div className='trip-summary-scheduled-date'>
            <h4 className={TITLE_CLASSES} onClick={() => setCurrentStep(2)}>
              {trips.scheduledPickup}
            </h4>
            <div className='trip-summary-scheduled rp-pt4'>
              <div className='column-gap-8 flex items-center'>
                <Calendar3 className='icon--20x20 svg-stroke-grey8' />
                <p className='font-grey8 rp-my0 text-center text-md'>
                  {`${moment(dateTime.date).format('DD/MM/YYYY')}`}
                </p>
              </div>
              <div className='column-gap-8 flex items-center'>
                <Clock className='icon--20x20 svg-stroke-grey8' />
                <p className='font-grey8 rp-my0 text-center text-md'>{`${moment(dateTime.time).format('HH:mm')}`}</p>
              </div>
            </div>
          </div>
        )}
        <div className='trip-summary-estimated-fare'>
          <h4 className={TITLE_CLASSES} onClick={() => setCurrentStep(2)}>
            {trips.estimatedFare}
          </h4>
          <p className='font-black3 font-medium rp-mt4 rp-my0 text-center text-md'>
            {product ? `$${(product.fare / 100).toFixed(2)}` : '$0.00'}
          </p>
        </div>
      </div>
      {(!!accessibilities.length || specialInstructions) && (
        <div className='rp-mt24'>
          <Divider />
          <div className='rp-pt16'>
            <h4 className={TITLE_CLASSES} onClick={() => setCurrentStep(2)}>
              {trips.specialRequirements}
            </h4>
            <div className='rp-accessibilities-container rp-mt12'>
              {!!accessibilities.length &&
                accessibilities.map(accessibility => (
                  <p className='rp-my0 text-dark text-sm' key={accessibility}>
                    {accessibility}
                  </p>
                ))}
              {specialInstructions && (
                <div className='flex items-center'>
                  <div className='rp-icon-container'>
                    <ExclamationCircle2 className='icon--18x18 svg-color-green1' />
                  </div>
                  <p className='rp-my0 text-dark text-sm'>{specialInstructions}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ConfirmAndPay



// WEBPACK FOOTER //
// ./src/components/riders/rider-portal/trips/create/Step3/ConfirmAndPay.tsx